// Catalog-card "Add to cart" for /pages/coffee (blendin-coffee-tier sections).
// Every retail coffee is single-variant, so each card adds its sole/first
// available variant at quantity 1 via /cart/add.js — no PDP visit, no variant
// picker. Feedback mirrors the self-contained pattern in blendin-wholesale-shop.js
// (inline button state + manual .bic-header__cart-count update), plus a branded
// slide-in toast. No Dawn cart-notification/drawer is rendered on this storefront
// (custom blendin-header + cart_type "notification"), so we manage our own UI.
//
// Bound via event delegation on document, so it covers all tier sections on the
// page and no-ops anywhere a .bic-card__add button is absent.

(function () {
  var REVERT_MS = 1400; // how long the button shows "Added" before reverting
  var TOAST_MS = 3500; // how long the toast stays up
  var HIDE_MS = 350; // fade-out length (just past --bic-duration-medium, 300ms)
  var toastEl = null;
  var toastInner = null;
  var toastTimer = null;
  var hideTimer = null;

  function syncHeaderCount(itemCount) {
    document.querySelectorAll('.bic-header__cart-count').forEach(function (el) {
      el.textContent = itemCount;
      // The mobile cart-icon badge hides itself when empty; keep it in sync.
      if (el.classList.contains('bic-header__cart-badge')) {
        el.classList.toggle('is-empty', itemCount === 0);
      }
    });
  }

  function refreshCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) { syncHeaderCount(cart.item_count); })
      .catch(function () { /* no-op; non-fatal */ });
  }

  function cartUrl() {
    var link = document.querySelector('.bic-header__cart-count');
    if (link && link.closest('a') && link.closest('a').getAttribute('href')) {
      return link.closest('a').getAttribute('href');
    }
    return '/cart';
  }

  function ensureToast() {
    if (toastEl) return;
    toastEl = document.createElement('div');
    toastEl.className = 'bic-card-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.hidden = true;
    toastInner = document.createElement('div');
    toastInner.className = 'bic-card-toast__inner';
    toastEl.appendChild(toastInner);
    document.body.appendChild(toastEl);
  }

  // opts: { eyebrow, title, image (optional), link (optional) }
  function showToast(opts) {
    ensureToast();
    while (toastInner.firstChild) toastInner.removeChild(toastInner.firstChild);

    if (opts.image) {
      var img = document.createElement('img');
      img.className = 'bic-card-toast__img';
      img.src = opts.image;
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      toastInner.appendChild(img);
    }

    var text = document.createElement('div');
    text.className = 'bic-card-toast__text';
    var eyebrow = document.createElement('span');
    eyebrow.className = 'bic-card-toast__eyebrow';
    eyebrow.textContent = opts.eyebrow;
    var title = document.createElement('span');
    title.className = 'bic-card-toast__title';
    title.textContent = opts.title;
    text.appendChild(eyebrow);
    text.appendChild(title);
    toastInner.appendChild(text);

    if (opts.link) {
      var a = document.createElement('a');
      a.className = 'bic-card-toast__link';
      a.href = opts.link;
      a.textContent = 'View cart →';
      toastInner.appendChild(a);
    }

    toastEl.hidden = false;
    // Force reflow so the transition runs from the hidden state.
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');

    clearTimeout(toastTimer);
    clearTimeout(hideTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
      // Restore hidden once the fade-out finishes. Without this the dismissed
      // toast stays a transparent fixed strip over the mobile viewport, and
      // its stretched cart link keeps catching taps (also keeps the link out
      // of the tab order between toasts).
      hideTimer = setTimeout(function () {
        toastEl.hidden = true;
      }, HIDE_MS);
    }, TOAST_MS);
  }

  function errorMessage(result) {
    if (result.data && (result.data.description || result.data.message)) {
      return result.data.description || result.data.message;
    }
    if (result.status === 422) {
      return 'This item may be out of stock or unavailable in your region.';
    }
    if (result.status === 404) {
      return 'This product is no longer available.';
    }
    return 'We could not add this item. Please try again, or refresh the page.';
  }

  function handleAdd(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId || btn.disabled) return;

    var labelEl = btn.querySelector('.bic-card__add-label') || btn;
    var originalLabel = labelEl.textContent;
    btn.disabled = true;
    btn.classList.add('is-loading');
    labelEl.textContent = 'Adding…';

    var body = new URLSearchParams();
    body.append('id', variantId);
    body.append('quantity', '1');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'Accept': 'application/json'
      },
      body: body.toString()
    })
      .then(function (res) {
        return res.text().then(function (text) {
          var data = null;
          try { data = text ? JSON.parse(text) : null; } catch (_) { /* non-JSON */ }
          return { ok: res.ok, status: res.status, data: data, text: text };
        });
      })
      .then(function (result) {
        if (!result.ok || !result.data) {
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('[coffee-add] add-to-cart failed', result.status, result.text);
          }
          throw new Error(errorMessage(result));
        }
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        labelEl.textContent = 'Added';
        showToast({
          eyebrow: 'Added to cart',
          title: btn.getAttribute('data-product-title') || 'Item',
          image: btn.getAttribute('data-product-image') || '',
          link: cartUrl()
        });
        return refreshCount().then(function () {
          setTimeout(function () {
            btn.classList.remove('is-added');
            labelEl.textContent = originalLabel;
            btn.disabled = false;
          }, REVERT_MS);
        });
      })
      .catch(function (err) {
        btn.classList.remove('is-loading');
        labelEl.textContent = originalLabel;
        btn.disabled = false;
        showToast({ eyebrow: 'Couldn’t add', title: err.message || 'Something went wrong.' });
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.bic-card__add');
    if (!btn) return;
    e.preventDefault();
    handleAdd(btn);
  });
})();
